import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Check, Lock, Loader2, ArrowRightLeft, Play } from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { safeStorage } from '../lib/safeStorage';

interface CalendarModalProps {
  onClose: () => void;
  isLightMode: boolean;
  getStats: () => any;
}

export function CalendarModal({ onClose, isLightMode, getStats }: CalendarModalProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [challengeData, setChallengeData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const startDate = new Date('2026-04-03T00:00:00Z');
  
  // Create a normalized "today" in local time but matching the game's date logic
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  const stats = getStats();
  const wonDates = stats.wonDates || [];

  const prevMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const minMonth = new Date(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1);
    if (newMonth >= minMonth) {
      setCurrentMonth(newMonth);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = async (dateStr: string, isLocked: boolean) => {
    if (isLocked) return;
    
    setSelectedDate(dateStr);
    setLoading(true);
    try {
      const res = await fetch(`/api/challenge/${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        setChallengeData(data);
      }
    } catch (e) {
      console.error("Failed to load challenge details", e);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth.getFullYear(), currentMonth.getMonth());

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null); // Empty slots before the 1st
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
  }

  const monthName = currentMonth.toLocaleString('default', { month: 'long' });
  const yearName = currentMonth.getFullYear();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      
      {/* Calendar Grid Modal */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className={cn(
          "relative w-full max-w-md rounded-3xl p-6 sm:p-8 border shadow-2xl overflow-y-auto max-h-[90vh] transition-colors",
          isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#2a2a2a] border-white/10"
        )}
      >
        <button 
          onClick={onClose} 
          className={cn(
            "absolute top-6 right-6 p-2 rounded-full transition-colors",
            isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
          )}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className={cn("text-2xl font-bold mb-6 text-center", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
          Archive
        </h2>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={prevMonth} 
            disabled={currentMonth.getFullYear() === startDate.getUTCFullYear() && currentMonth.getMonth() === startDate.getUTCMonth()}
            className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <span className="text-lg font-bold">
            {monthName} {yearName}
          </span>
          <button onClick={nextMonth} className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5">
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 mb-2 text-center text-xs font-bold text-gray-500">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((date, i) => {
            if (!date) {
              return <div key={`empty-${i}`} className="h-10 sm:h-12" />;
            }
            
            // Format to UTC string to match local game dates
            const dateStr = [
              date.getFullYear(),
              String(date.getMonth() + 1).padStart(2, '0'),
              String(date.getDate()).padStart(2, '0')
            ].join('-');

            const isBeforeStart = date < startDate;
            // A day is considered locked if it's strictly > today's date
            // We use todayStr to compare
            const todayStr = [
              today.getUTCFullYear(),
              String(today.getUTCMonth() + 1).padStart(2, '0'),
              String(today.getUTCDate()).padStart(2, '0')
            ].join('-');
            
            const isFuture = dateStr > todayStr;
            const isLocked = isBeforeStart || isFuture;
            const hasWon = wonDates.includes(dateStr);

            let bgClass = "bg-transparent border border-black/10 dark:border-white/10";
            let textClass = isLightMode ? "text-black" : "text-white";

            if (isLocked) {
              bgClass = "bg-black/5 dark:bg-white/5 border-transparent opacity-50";
              textClass = "text-gray-500";
            } else if (hasWon) {
              bgClass = "bg-[#ff0080] border-transparent shadow-md shadow-[#ff0080]/20 text-white";
              textClass = "text-white";
            }

            return (
              <button
                key={dateStr}
                onClick={() => handleDayClick(dateStr, isLocked)}
                disabled={isLocked}
                className={cn(
                  "h-10 sm:h-12 rounded-xl flex items-center justify-center text-sm font-bold transition-all relative overflow-hidden",
                  bgClass,
                  textClass,
                  !isLocked && (isLightMode ? "hover:border-[#ff0080] hover:text-[#ff0080]" : "hover:border-[#ff0080] hover:text-[#ff0080]")
                )}
              >
                {hasWon ? <Check className="w-5 h-5 opacity-80" /> : date.getDate()}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Date Detail Modal */}
      <AnimatePresence>
        {selectedDate && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedDate(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-sm rounded-3xl p-6 border shadow-2xl transition-colors text-center",
                isLightMode ? "bg-white border-black/10" : "bg-[#1a1a1a] border-white/10"
              )}
            >
              <button 
                onClick={() => setSelectedDate(null)} 
                className={cn(
                  "absolute top-4 right-4 p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-4 h-4" />
              </button>

              <div className="mb-6 mt-2 text-xs font-bold uppercase tracking-widest text-gray-500">
                {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-24">
                  <Loader2 className="w-8 h-8 animate-spin text-[#ff0080]" />
                </div>
              ) : challengeData ? (
                <>
                  <div className={cn(
                    "p-5 rounded-2xl border text-center mb-6",
                    isLightMode ? "bg-[#f9f9f9] border-black/5" : "bg-black/40 border-white/5"
                  )}>
                    <div className={cn("text-xl font-black mb-2", isLightMode ? "text-black" : "text-white")}>
                      {challengeData.startArtist}
                    </div>
                    <div className="flex justify-center my-2">
                      <ArrowRightLeft className="w-5 h-5 text-[#ff0080] rotate-90" />
                    </div>
                    <div className={cn("text-xl font-black mt-2", isLightMode ? "text-black" : "text-white")}>
                      {challengeData.targetArtist}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigate(`/${selectedDate}`);
                      onClose();
                    }}
                    className="w-full bg-[#ff0080] text-white py-4 rounded-xl font-black text-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#ff0080]/20"
                  >
                    <Play className="w-5 h-5 fill-current" /> Play Archive
                  </button>
                </>
              ) : (
                <div className="py-8 text-gray-500">Challenge not found.</div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
